import React from 'react'

import { FiltersSidebar } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { array, bool, func, number, oneOfType, string, object } from 'prop-types'

import WithListingPageContext from 'Pods/ListingPage/Components/WithListingPageContext'
import i18n from 'Pods/Shared/I18n'

import getSelectedFilters from 'Utils/getSelectedFilters'
import useBreakpoint from 'Utils/useBreakpoint'

import PriceSlider from '../../../Price/Components/PriceSlider'
import SubscriptionTier from '../../../Price/Components/SubscriptionTier'
import {
  getPriceRangeForTier,
  getPricingForTier,
  getSelectedTier,
  TIER_ALL,
} from '../../../Price/subscriptionTierFilters'

function RemainingPriceFilters({
  currentSelectedFilters,
  initialFrom = null,
  initialTo = null,
  maxPriceThreshold,
  minPriceThreshold,
  setMultipleFilters,
  setSelectedFilters,
  currentFilters,
  rangeFilters,
  selectedFilters,
  setShouldResetPriceRange,
  trackFilterChange,
  showSubscriptionTier = false,
  locale = 'en',
}) {
  const windowSize = useBreakpoint()
  const shouldDisplayTitleDivider = windowSize === 'xs'

  const quickSelectionsLabel = i18n.t('listingPage.filters.quickSelections', { ns: 'rails', lng: locale })

  function removeFreeFilter() {
    const isFreeFilterActive = currentSelectedFilters.includes('free')
    if (isFreeFilterActive) {
      const newFiltersEntries = currentSelectedFilters
        .filter((value) => value && value !== 'free')
        .map((value) => [value, '1'])
      const newFilters = Object.fromEntries(newFiltersEntries)

      setMultipleFilters({ name: 'pricing', value: newFilters })
    }
  }

  function handleFilterChange(_, value, isOn) {
    const selectedPricingFilters = getSelectedFilters(selectedFilters.pricing || [])
    const selectedPriceRangeFilters = selectedFilters.priceRange || []

    if (value.includes('range_') || value.includes('more_than_')) {
      const newPriceRangeFilters = isOn
        ? [...selectedPriceRangeFilters, value]
        : selectedPriceRangeFilters.filter((item) => item !== value)
      removeFreeFilter()
      setSelectedFilters({ name: 'priceRange', value: newPriceRangeFilters })

      if (isOn) {
        trackFilterChange({ name: 'priceRange', value })
      } else {
        window.removeCurrentSearchFilter({ name: 'priceRange', value })
      }

      const rangeFiltersList = rangeFilters.map((item) => item.value)
      if (!rangeFiltersList.includes(value)) {
        setShouldResetPriceRange(true)
      }

      return
    }

    if (isOn && value === 'free') {
      setSelectedFilters({ name: 'priceRange', value: [] })
      setShouldResetPriceRange(true)
    }

    const filtersToRemove = (() => {
      if (value === 'free') {
        return ['exclude_free', 'subscribed']
      }
      if (value === 'exclude_free') {
        return ['free']
      }
      if (value === 'subscribed') {
        return ['free']
      }
      return []
    })()
    const newPricingFiltersEntries = (() => {
      if (isOn) {
        if (filtersToRemove.length > 0) {
          return [...selectedPricingFilters.filter((item) => !filtersToRemove.includes(item)), value]
        }

        return [...selectedPricingFilters, value]
      }

      return selectedPricingFilters.filter((item) => item !== value)
    })()
    const newPricingFilters = Object.fromEntries(newPricingFiltersEntries.map((item) => [item, '1']))
    setSelectedFilters({ name: 'pricing', value: newPricingFilters })

    if (isOn) {
      trackFilterChange({ name: 'pricing', value })
    } else {
      window.removeCurrentSearchFilter({ name: 'pricing', value })
    }
  }

  function handleTierChange(tier) {
    const selectedPriceRangeFilters = selectedFilters.priceRange || []
    const nextPriceRange = getPriceRangeForTier(tier, selectedPriceRangeFilters)
    const nextPricing = getPricingForTier(getSelectedFilters(selectedFilters.pricing || []))

    // One update for both filters so the request and the URL are written once.
    setSelectedFilters({
      name: 'priceRange',
      value: nextPriceRange,
      newObject: { ...selectedFilters, pricing: nextPricing, priceRange: nextPriceRange },
    })

    if (tier === TIER_ALL) {
      setShouldResetPriceRange(true)
    }

    trackFilterChange({ name: 'subscriptionTier', value: tier })
  }

  return (
    <div className="remaining-filters__price">
      <PriceSlider
        initialFrom={initialFrom}
        initialTo={initialTo}
        maxPriceThreshold={maxPriceThreshold}
        minPriceThreshold={minPriceThreshold}
        setSelectedFilters={setSelectedFilters}
        removeFreeFilter={removeFreeFilter}
        withTitleDivider={shouldDisplayTitleDivider}
      />
      <div className="remaining-filters__quick-selections">
        <FiltersSidebar
          isCollapsible={false}
          filters={[{ filters: rangeFilters, label: quickSelectionsLabel }]}
          selectedFilters={currentSelectedFilters}
          onFiltersChange={handleFilterChange}
          isControlled
          useNewCheckboxes
          dividerClassName="remaining-filters__divider"
          hideShowMore
        />
      </div>
      {showSubscriptionTier && (
        <SubscriptionTier
          selectedTier={getSelectedTier(
            selectedFilters.priceRange || [],
            getSelectedFilters(selectedFilters.pricing || [])
          )}
          onChange={handleTierChange}
          name="remaining-subscription-tier"
          dividerClassName="remaining-filters__divider"
          locale={locale}
        />
      )}
      <FiltersSidebar
        isCollapsible={false}
        filters={[{ filters: currentFilters, label: null }]}
        selectedFilters={currentSelectedFilters}
        onFiltersChange={handleFilterChange}
        isControlled
        hideLastDivider
        useNewCheckboxes
        hideShowMore
      />
    </div>
  )
}

RemainingPriceFilters.propTypes = {
  currentSelectedFilters: array.isRequired,
  initialFrom: oneOfType([string, number]),
  initialTo: oneOfType([string, number]),
  maxPriceThreshold: number.isRequired,
  minPriceThreshold: number.isRequired,
  setMultipleFilters: func.isRequired,
  setSelectedFilters: func.isRequired,
  currentFilters: array.isRequired,
  rangeFilters: array.isRequired,
  selectedFilters: object.isRequired,
  setShouldResetPriceRange: func.isRequired,
  trackFilterChange: func.isRequired,
  showSubscriptionTier: bool,
  locale: string,
}

const mapStateToProps = ({
  maxPriceThreshold,
  minPriceThreshold,
  selectedFilters,
  setMultipleFilters,
  setSelectedFilters,
  filtersOptions,
  setShouldResetPriceRange,
  trackFilterChange,
  locale,
  subscriptionFlowEnabled,
  premiumListingLabelEnabled,
  subscriptionV17CatalogEnabled,
}) => {
  const freeOnlyLabel = i18n.t('listingPage.filters.price.freeOnly', { ns: 'rails', lng: locale })
  const onSaleLabel = i18n.t('listingPage.filters.price.onSale', { ns: 'rails', lng: locale, percentage: 70 })

  const rangeFilters = filtersOptions.priceRange.filters || []
  const excludeFreeFilter = filtersOptions.pricing.filters.find(({ value }) => value === 'exclude_free')
  const pricingFilters = (filtersOptions.pricing.filters || [])
    .map((item) => {
      if (item.value === 'free') {
        return { ...item, label: freeOnlyLabel }
      }

      if (item.value === 'sale_off') {
        return { ...item, label: onSaleLabel }
      }

      if (item.value === 'subscribed') {
        return subscriptionFlowEnabled ? item : null
      }

      return null
    })
    .sort((a, b) => b?.label.localeCompare(a?.label))
  const rangeFiltersList = rangeFilters.map(({ value }) => value)
  const selectedPricingFilters = selectedFilters.pricing || []
  const selectedPriceRangeFilters = selectedFilters.priceRange || []
  const customPriceRange = selectedPriceRangeFilters.find((value) => !rangeFiltersList.includes(value))
  const [initialFrom, initialTo] = (() => {
    if (customPriceRange) {
      const splitValue = customPriceRange.split('_').slice(1)
      return [splitValue[0], splitValue[1]]
    }

    return [undefined, undefined]
  })()

  const currentFilters = [...pricingFilters, excludeFreeFilter].filter(Boolean)
  const currentSelectedFilters = [...selectedPriceRangeFilters, ...getSelectedFilters(selectedPricingFilters)].filter(
    Boolean
  )

  return {
    currentFilters,
    currentSelectedFilters,
    initialFrom,
    initialTo,
    maxPriceThreshold,
    minPriceThreshold,
    setMultipleFilters,
    setSelectedFilters,
    rangeFilters,
    selectedFilters,
    setShouldResetPriceRange,
    trackFilterChange,
    locale,
    // Two independent reasons a tier pick would dead-end, so both are required:
    // - premiumListingLabelEnabled: without the experiment the backend forces the essentials band
    //   on any subscribed search, so Premium returns nothing and All/Essentials are the same set.
    // - subscriptionV17CatalogEnabled: with the catalog off, Item#eligible_for_subscription? falls
    //   back to the essentials band (price < $10), so no item is eligible at the Premium prices at
    //   all - the band is never forced, there is simply nothing in it.
    showSubscriptionTier: subscriptionFlowEnabled && premiumListingLabelEnabled && subscriptionV17CatalogEnabled,
  }
}

export default WithListingPageContext(mapStateToProps)(RemainingPriceFilters)
